import { DEFAULT_CURRENCY_CODE, WEALTH_ROUTES } from '@egw/constants'
import { Trans, useTranslation } from '@egw/i18n'
import { Currency } from '@egw/utils'
import { InformationCircleIcon as InfoIconOutline } from '@heroicons/react/24/outline'
import { type FC, useMemo, useState } from 'react'
// import Popover from 'components/layout/Popover'
import { Button, ButtonVariant, ModalDrawer } from '@egw/components'
import { configClient } from '@egw/config'
import { useRouter } from '@egw/router'
import { useTransferContextProvider } from '../../context'
import { type Config, ETransferType } from '../../types'
import { checkIfBankAccountPosition } from '../../utils'

export enum ETransferFlow {
  OUTBOUND = 'OUTBOUND',
  INBOUND = 'INBOUND',
  NA = 'Not Applicable',
}

interface TransferLimitModalInterface {
  transferFlow: ETransferFlow
  dailyLimit?: number
  monthlyLimit?: number
}

const TransferLimitModal: FC<TransferLimitModalInterface> = ({ transferFlow, dailyLimit, monthlyLimit }) => {
  const { t } = useTranslation('features-transfer')

  const [showModal, setShowModal] = useState<boolean>(false)

  if (!dailyLimit || !monthlyLimit) return null

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={() => {
          setShowModal(true)
        }}
        id="transfer-limits-popover"
        data-testid="transfer-limits-popover"
      >
        <TransferLimitDisplay />
      </button>

      <ModalDrawer
        onClose={() => {
          setShowModal(false)
        }}
        title={t(`transfer.transfer_limits.${transferFlow}.title`)}
        isOpen={showModal}
      >
        <TransferLimits
          transferFlow={transferFlow}
          onClickCb={() => {
            setShowModal(false)
          }}
          dailyLimit={dailyLimit}
          monthlyLimit={monthlyLimit}
        />
      </ModalDrawer>
    </div>
  )
}

const TransferLimits: FC<{
  transferFlow: ETransferFlow
  dailyLimit: number
  monthlyLimit: number
  onClickCb: () => void
}> = ({ dailyLimit, monthlyLimit, onClickCb, transferFlow }) => {
  const config = configClient.getConfig() as Config
  const { t } = useTranslation('features-transfer')
  const { navigate } = useRouter()
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col">
        <p className="text-lg text-header ">{t('transfer.transfer_limits.header_1')}</p>

        <div className="flex flex-row justify-between text-header text-base">
          <span className="font-semibold">{t('transfer.transfer_limits.limit')}</span>
          <span className="font-noto font-semibold">
            {t(`currency.${DEFAULT_CURRENCY_CODE}`, {
              amount: Currency.integerCents.omitSign(dailyLimit),
            })}
          </span>
        </div>
      </div>

      <div className="border-b border-light" />

      <div className="flex flex-col">
        <p className="text-lg text-header">{t('transfer.transfer_limits.header_2')}</p>

        <div className="flex flex-row justify-between text-header text-base">
          <span className="font-semibold">{t('transfer.transfer_limits.limit')}</span>
          <span className="font-noto font-semibold">
            {t(`currency.${DEFAULT_CURRENCY_CODE}`, {
              amount: Currency.integerCents.omitSign(monthlyLimit),
            })}
          </span>
        </div>
      </div>

      <div className="border-b border-light" />
      <div className="flex flex-col gap-4">
        <p className="text-base text-header ">{t(`transfer.transfer_limits.${transferFlow}.description`)}</p>
        {transferFlow === ETransferFlow.OUTBOUND ? (
          <Button
            type="button"
            variant={ButtonVariant.Secondary}
            onClick={() => {
              navigate(WEALTH_ROUTES.TRANSFER_WIRE.path)
            }}
          >
            {t('transfer.transfer_limits.OUTBOUND.cta')}
          </Button>
        ) : (
          <Button
            type="button"
            variant={ButtonVariant.Secondary}
            onClick={() => {
              window.open(config.zendeskArticles.wireTransferHowTo, '_blank')
              onClickCb()
            }}
          >
            {t('transfer.transfer_limits.INBOUND.cta')}
          </Button>
        )}
      </div>
    </div>
  )
}

const TransferLimitDisplay = () => {
  const { t } = useTranslation('features-transfer')
  return (
    <div className="text-action text-base flex flex-row gap-2">
      <Trans
        t={t}
        className="text-action text-base flex flex-row gap-2"
        i18nKey="transfer.transfer_limits.display"
        components={{
          1: <InfoIconOutline className="flex-shrink-0 w-6 h-6 text-action block" />,
        }}
      />
    </div>
  )
}

export const TransferLimitsSection: React.FC = () => {
  const { fromFieldOption, transferType, fetchedTransferLimits } = useTransferContextProvider()

  const { transferPopoverProps, showTransferLimits } = useMemo((): {
    showTransferLimits: boolean
    transferPopoverProps: TransferLimitModalInterface
  } => {
    const emptyTransferPopoverProps = {
      showTransferLimits: false,
      transferPopoverProps: {
        transferFlow: ETransferFlow.NA, // Irrelevant
      },
    }

    if (fetchedTransferLimits?.__typename === 'AchLimits') {
      const isFromEvergreen = fromFieldOption && checkIfBankAccountPosition(fromFieldOption)
      const incomingLimits = fetchedTransferLimits.credit
      const outgoingLimits = fetchedTransferLimits.debit

      const limits = isFromEvergreen ? outgoingLimits : incomingLimits
      const transferFlow = isFromEvergreen ? ETransferFlow.OUTBOUND : ETransferFlow.INBOUND

      return {
        showTransferLimits: transferType === ETransferType.ACH,
        transferPopoverProps: {
          ...limits,
          transferFlow,
        },
      }
    }

    return emptyTransferPopoverProps
  }, [fromFieldOption, transferType, fetchedTransferLimits])

  return showTransferLimits && <TransferLimitModal {...transferPopoverProps} />
}
